import { requestFetch, type TransportFetch } from "@/transport/http"
import { abortable } from "@/utils/abortable"

export type HealthProbeFailure = {
  reason: "timeout" | "cancelled" | "network" | "http" | "unhealthy" | "wrong-machine" | "invalid-response"
  http_status?: number
}

export type HealthProbeResult = { healthy: true; version?: string } | ({ healthy: false } & HealthProbeFailure)

// Health is public on both the runtime and supervisor. Do not put authenticated
// request preparation, preflight-only headers, or route recovery on this path.
export async function requestHealth(
  url: string,
  options?: { signal?: AbortSignal; expectedMachineName?: string; fetch?: TransportFetch },
): Promise<HealthProbeResult> {
  const signal = options?.signal
  try {
    signal?.throwIfAborted()
    const response = await abortable(requestFetch(`${url.replace(/\/+$/, "")}/global/health`, {
      cache: "no-store",
      credentials: "omit",
      signal,
    }, options?.fetch).then(response => {
      if (signal?.aborted) {
        void response.body?.cancel().catch(() => {})
        signal.throwIfAborted()
      }
      return response
    }), signal)
    if (!response.ok) {
      void response.body?.cancel().catch(() => {})
      return { healthy: false, reason: "http", http_status: response.status }
    }
    const health: unknown = await abortable(response.json(), signal)
    signal?.throwIfAborted()
    if (!health || typeof health !== "object" || !("healthy" in health) || typeof health.healthy !== "boolean") {
      return { healthy: false, reason: "invalid-response" }
    }
    if (!health.healthy) return { healthy: false, reason: "unhealthy" }
    if (options?.expectedMachineName && (!("machine_name" in health) || health.machine_name !== options.expectedMachineName)) {
      return { healthy: false, reason: "wrong-machine" }
    }
    return { healthy: true, ...("version" in health && typeof health.version === "string" ? { version: health.version } : {}) }
  } catch (error) {
    if (signal?.aborted) {
      return { healthy: false, reason: signal.reason?.name === "TimeoutError" ? "timeout" : "cancelled" }
    }
    // Browser fetch cannot distinguish CORS rejection from network interruption.
    return { healthy: false, reason: error instanceof SyntaxError ? "invalid-response" : "network" }
  }
}
